import b from 'b_';
import React from 'react';
import { useSelector } from 'react-redux';
import { injectIntl, WrappedComponentProps as WithIntl } from 'react-intl';

import type { LessonProblem, ProblemResults, StudentProblemResult } from '@yandex-int/k-common/typings';
import { PastesIcon, DetectPasteTooltip } from '@edu-frontend/modules/src/features/pastesHistory';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';

import { Text } from 'platform-components';
import { getFeatureFlags } from 'platform-components/src/components/app-factory/selectors';

import { isOpenAnswer, isRobot, isTheory as isTheoryUtil } from 'store/problems/utils';
import { useBunkerOnboarding, DETECT_PASTE_ONBOARDING_KEY } from 'hooks/useBunkerOnboarding';

import {
    OpenAnswerLink,
    ProblemStatus,
    RobotSamples,
    StudentAttempts,
    StudentTime,
    StudentPoints,
    SuccessfulStudents,
    TeamTime,
    AnswerControls,
} from '../__stat-item';
import { StudentAttemptsTextProps } from '../__stat-item/_student-attempts-text/use-student-attempts-text';

import './lesson-content__stat.scss';

interface OwnProps {
    fullScreen: boolean;
    isEvaluable: boolean;
    isEvaluated: boolean;
    studentProblemResult: StudentProblemResult | null;
    lessonProblemResult: ProblemResults;
    problemLink: LessonProblem;
    isParent: boolean;
    isLessonScoreAvailable: boolean;
    isPointsMode: boolean;
    shouldDisplayAttempts: boolean;
    index: number;
    correctAnswersAvailable: boolean;
    renderStudentAttemptsText: (props: StudentAttemptsTextProps) => React.ReactNode;
}

type Props = OwnProps & WithIntl;

// eslint-disable-next-line complexity
export const LessonContentStat = (props: Props) => {
    const {
        fullScreen,
        isParent,
        studentProblemResult,
        lessonProblemResult,
        isEvaluated,
        problemLink,
        isLessonScoreAvailable,
        isPointsMode,
        shouldDisplayAttempts,
        index,
        correctAnswersAvailable,
        renderStudentAttemptsText,
        intl,
    } = props;

    const isTheory = isTheoryUtil(problemLink?.problem || null);
    const shouldDisplayPoints = isPointsMode && !isTheory;
    const shouldDisplayAttemptsForProblem = shouldDisplayAttempts && !isTheory;
    const shouldShowTeamSpentTime = !isParent && !isOpenAnswer(problemLink?.problem);

    const showPastesFlag = useSelector(getFeatureFlags)?.showPastes;

    const { openOnboarding } = useBunkerOnboarding({ id: DETECT_PASTE_ONBOARDING_KEY });

    return (
        <div className={b('lesson-content', 'stat', { 'full-screen': fullScreen })}>
            <div className="lesson-content__stat-details">
                <div className="lesson-content__stat-index">
                    <Text id="lessonContent.itemIndex" size={Text.SIZE.XL} values={{ index: index + 1 }} />
                </div>
                <ProblemStatus problemLink={problemLink} studentProblemResult={studentProblemResult} />
                {correctAnswersAvailable && (
                    <AnswerControls problemLink={problemLink} studentProblemResult={studentProblemResult} />
                )}
                {isRobot(problemLink?.problem) && <RobotSamples studentProblemResult={studentProblemResult} />}
                {shouldDisplayPoints && (
                    <StudentPoints
                        isLessonScoreAvailable={isLessonScoreAvailable}
                        problemLink={problemLink}
                        studentProblemResult={studentProblemResult}
                    />
                )}
                {shouldDisplayAttemptsForProblem && (
                    <StudentAttempts>
                        {renderStudentAttemptsText({
                            problemLink,
                            studentProblemResult,
                        })}
                    </StudentAttempts>
                )}
                <StudentTime isEvaluated={isEvaluated} studentProblemResult={studentProblemResult} />
                {isOpenAnswer(problemLink?.problem) && (
                    <OpenAnswerLink
                        isLessonScoreAvailable={isLessonScoreAvailable}
                        studentProblemResult={studentProblemResult}
                    />
                )}
                {shouldShowTeamSpentTime && (
                    <TeamTime isEvaluated={isEvaluated} lessonProblemResult={lessonProblemResult} />
                )}
                {!isParent && (
                    <SuccessfulStudents
                        lessonProblemResult={lessonProblemResult}
                        problemLink={problemLink}
                        studentProblemResult={studentProblemResult}
                    />
                )}
                {showPastesFlag && !isParent && studentProblemResult?.cheating.pasted && (
                    <ThemeProvider isRoot={false} theme={hrLight}>
                        <div className="lesson-content__has-pastes">
                            <PastesIcon />
                            <Text id="pasteHistory.hasPastes" />
                            <DetectPasteTooltip
                                className="lesson-content__has-pastes-tooltip-icon"
                                getIntlText={(id, values) => intl.formatMessage({ id }, values)}
                                onTooltipLinkClick={() => openOnboarding()}
                                tooltipClassName="lesson-content__has-pastes-tooltip"
                                tooltipDirection="top-end"
                            />
                        </div>
                    </ThemeProvider>
                )}
            </div>
        </div>
    );
};

export default injectIntl(LessonContentStat);
